<template>
  <section class="debug card">
    <h2 class="debug__title">HE debug</h2>
    <p class="debug__hint">
      Shown because the URL has <code>?debug</code>. The same data is posted to <code>/he-log</code> (pod logs, <code>grep HE_</code>).
      <a class="text-link" href="/he-headers" target="_blank" rel="noopener">Open /he-headers</a>
    </p>
    <dl class="debug__grid">
      <dt>page</dt><dd>{{ page.protocol }}//{{ page.host }}</dd>
      <dt>brand</dt><dd>{{ brandConfig.brand }} · SID {{ brandConfig.networkServiceId }} · product {{ brandConfig.productId }}</dd>
      <dt>premium</dt><dd>{{ premium.mode }} · {{ premium.env }} · {{ premium.baseUrl || '(no base url)' }}</dd>
      <dt>ext_ref</dt><dd>{{ extRef }}</dd>
      <dt>step</dt><dd>{{ step }} · msisdn {{ msisdn || '-' }}</dd>
      <dt>Premium HE</dt><dd>identified {{ heIdentified }} · token {{ heToken ? 'yes' : 'no' }} <span class="debug__note">(decides JOIN vs PIN)</span></dd>
      <dt>page headers</dt><dd>{{ headerEnriched ? 'number in page request' : 'none' }} <span class="debug__note">(nginx, http page loads only; prefill only, not the flow)</span></dd>
      <dt>evina</dt><dd>armed {{ evina.armed }} · gateway-load {{ evina.gatewayLoaded }}</dd>
      <dt>prelander</dt><dd>{{ prelanderRow }}</dd>
      <dt>campaign</dt><dd>{{ campaignId || '-' }} <span class="debug__note">({{ attributionSource || 'none' }})</span> · click {{ clickId || '-' }}<template v-if="googleClickId"> · gclid</template> · publisher {{ publisherId || '-' }}<template v-if="subPublisherId"> / {{ subPublisherId }}</template></dd>
    </dl>
    <h3 class="debug__sub">Premium /he result (decides JOIN vs PIN)</h3>
    <pre class="debug__pre">{{ pretty(heResult) }}</pre>
    <h3 class="debug__sub">Page headers: nginx bootstrap (window.__WR_HE__)</h3>
    <pre class="debug__pre">{{ pretty(bootstrap) }}</pre>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useBrandStore } from '@/stores/brandStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { premiumMode, premiumBaseUrl, premiumEnv } from '@/services/telkomPremiumService'
import { prelanderRequested } from '@/functions/prelander'

const { brandConfig } = storeToRefs(useBrandStore())
const {
  step, msisdn, headerEnriched, heIdentified, heToken, heResult, extRef, evina,
  campaignId, clickId, googleClickId, publisherId, subPublisherId, attributionSource,
  prelanding, prelanderTiming
} = storeToRefs(useSubscriptionStore())

// Times from the pre-lander's first paint, to tune its two seconds on a handset.
const prelanderRow = computed(() => {
  if (!prelanderRequested()) return 'off'
  if (prelanding.value) return 'showing'
  const timing = prelanderTiming.value
  if (!timing) return 'ended (cancelled)'
  const ms = (value) => (value === null ? '-' : `${value} ms`)
  return `loaded ${ms(timing.loadedMs)} · identified ${ms(timing.identifiedMs)} · on screen ${ms(timing.shownMs)} · JOIN at ${ms(timing.advancedMs)}${timing.capped ? ' (cap)' : ''}`
})

const page = window.location
const premium = { mode: premiumMode(), env: premiumEnv(), baseUrl: premiumBaseUrl() }
// main.js copies the bootstrap before userAuthenticate.js consumes (and deletes) it.
const bootstrap = window.__WR_HE_SNAPSHOT__ ?? null

function pretty(value) {
  if (value === null || value === undefined) return '(none yet)'
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
</script>

<style scoped>
.debug {
  padding: var(--spacing-md) var(--spacing-lg);
  font-size: 0.8rem;
  word-break: break-all;
}

.debug__title {
  font-size: 1.05rem;
  margin-bottom: 6px;
}

.debug__hint {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: var(--spacing-sm);
}

.debug__grid {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 12px;
}

.debug__grid dt {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 700;
}

.debug__sub {
  font-size: 0.85rem;
  margin: var(--spacing-sm) 0 4px;
}

.debug__note {
  color: rgba(255, 255, 255, 0.55);
}

.debug__pre {
  white-space: pre-wrap;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  line-height: 1.45;
  padding: 8px 10px;
  border-radius: var(--radius-md);
  background: var(--color-field);
  border: 1px solid var(--color-field-border);
}
</style>
